// The Radix/shadcn Toaster at @/components/ui/toaster pairs with useToast(),
// which nothing in this app calls: every toast.success/error() everywhere
// (14 files) is sonner's own function, from the 'sonner' package directly.
// Mounting the wrong Toaster meant every one of those calls updated sonner's
// internal store with nothing subscribed to render it, so every send, save,
// resend and toggle failed or succeeded in total silence.
import { Toaster } from "@/components/ui/sonner"
import { Capacitor } from '@capacitor/core'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, Navigate, Outlet } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import { SelectedShowProvider } from '@/hooks/useSelectedShow';
import { NavigationGuardProvider } from '@/hooks/useNavigationGuard';
import ScrollToTop from './components/ScrollToTop';
import ProtectedRoute from '@/components/ProtectedRoute';
import ProfileCompletenessGate from '@/components/ProfileCompletenessGate';
import BottomNav from '@/components/home/BottomNav';
import OutboxSync from '@/components/OutboxSync';
import Home from '@/pages/Home';
import NewMeeting from '@/pages/NewMeeting';
import AcceptMeeting from '@/pages/AcceptMeeting';
import Shows from '@/pages/Shows';
import DeletedContacts from '@/pages/DeletedContacts';
import Admin from '@/pages/Admin';
import Profile from '@/pages/Profile';
import Templates from '@/pages/Templates';
import EditContact from '@/pages/EditContact';
import StuckCaptures from '@/pages/StuckCaptures';
import EditRep from '@/pages/EditRep';
import Login from '@/pages/Login';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';

// The one shared "which show" state for every page that needs it, so
// switching shows anywhere is reflected everywhere without a reload.
const SelectedShowLayout = () => (
  <SelectedShowProvider>
    <Outlet />
  </SelectedShowProvider>
);

/**
 * Admin is web only, never reachable from the iOS app, task: Capacitor
 * scaffolding (2026-09-08). CLAUDE.md's own split (section 2: "iOS app =
 * field capture. Web = management and admin.") already says this, but
 * nothing enforced it before there was an iOS build to enforce it against.
 * Checked by Capacitor.isNativePlatform(), not the existing isAdmin role
 * check: this has to hold even for the owner's own account signed into the
 * phone, since the point is which BUILD a screen is reachable from, not
 * who is logged into it. Redirects to Home rather than 404, matching how
 * ProtectedRoute already redirects an unauthorized visit elsewhere in this
 * router.
 */
const WebOnlyRoute = () =>
  Capacitor.isNativePlatform() ? <Navigate to="/" replace /> : <Outlet />;

/**
 * The app shell for every authenticated screen, task: bottom nav on every
 * screen (2026-09-08). NavigationGuardProvider has to wrap BottomNav too,
 * not just the page content: the nav's own Home/+/More buttons are exactly
 * the triggers the unsaved changes guard has to intercept. Identical nav on
 * every screen, on purpose: the owner's own reasoning is that a nav whose
 * meaning changes per screen is worse than one that never grows a fourth
 * button.
 */
const AppShellLayout = () => (
  <NavigationGuardProvider>
    <Outlet />
    <BottomNav />
    <OutboxSync />
  </NavigationGuardProvider>
);

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings } = useAuth();

  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept" element={<AcceptMeeting />} />
      <Route element={<ProtectedRoute unauthenticatedElement={<Navigate to="/login" replace />} />}>
        {/* Task: profile completeness gate (2026-09-15). Inside ProtectedRoute
            (so it only ever runs for a signed-in rep) and outside
            SelectedShowLayout/AppShellLayout (so a blocked rep never renders
            the bottom nav or a show picker for a capture flow they cannot
            use). Exempts /profile itself, see the component's own comment. */}
        <Route element={<ProfileCompletenessGate />}>
          <Route element={<SelectedShowLayout />}>
            <Route element={<AppShellLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/new" element={<NewMeeting />} />
              <Route path="/contacts/:id/edit" element={<EditContact />} />
              <Route path="/shows" element={<Shows />} />
              <Route path="/deleted-contacts" element={<DeletedContacts />} />
              <Route path="/stuck-captures" element={<StuckCaptures />} />
              <Route path="/profile" element={<Profile />} />
              <Route element={<WebOnlyRoute />}>
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/reps/:id/edit" element={<EditRep />} />
              </Route>
              <Route path="/templates" element={<Templates />} />
            </Route>
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <ScrollToTop />
          <AuthenticatedApp />
        </Router>
        <Toaster />
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App